import { Flex, Grid, GridItem, Icon, IconButton, Text } from '@chakra-ui/react';
import { AddressCard, Checkbox, Input } from '@shared/components';
import { maskDocument, maskTelephone } from '@shared/utils';
import { Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetUserInfo } from '../../hooks';
import { getAddressByPostalCode } from '../../services';

export const PaymentCreditCardAddress = () => {
  const { t } = useTranslation('cross-seller/payment');
  const hookForm = useFormContext();
  const sameAddress = hookForm.watch('sameAddress');
  const [postalCodePartial, setPostalCodePartial] = useState(false);

  const { data: userInfo } = useGetUserInfo();

  const currentCompany = userInfo?.UserCompany?.find(
    company => company.companyId === sessionStorage.getItem('companyId'),
  );

  const handleGetAddress = async () => {
    const postalCode = hookForm.getValues('postalCode');
    if (postalCode.length === 9) {
      const response = await getAddressByPostalCode(postalCode);

      if (response.erro) {
        hookForm.setError('postalCode', {
          message: t('cross-seller/payment:postalCodeError'),
        });
      } else {
        hookForm.clearErrors('postalCode');
      }

      if (response.logradouro === '' || response.bairro === '') {
        setPostalCodePartial(true);
      } else {
        setPostalCodePartial(false);
      }

      hookForm.setValue('street', response.logradouro);
      hookForm.setValue('district', response.bairro);
      hookForm.setValue('city', response.localidade);
      hookForm.setValue('state', response.uf);
    }
  };

  useEffect(() => {
    handleGetAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookForm.watch('postalCode')]);

  useEffect(() => {
    if (sameAddress) {
      const address = currentCompany?.Company?.addressMetadata;

      hookForm.setValue('postalCode', address?.zipCode);
      hookForm.setValue('number', address?.streetNumber);
      hookForm.setValue('complement', address?.complement);
      hookForm.setValue('city', address?.city);
      hookForm.setValue('state', address?.state);
      hookForm.setValue('street', address?.street);
      hookForm.setValue('district', address?.neighborhood);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAddress]);

  const handleClearPostalCode = () => {
    hookForm.setValue('postalCode', '');
    hookForm.setValue('street', '');
    hookForm.setValue('district', '');
    hookForm.setValue('city', '');
    hookForm.setValue('state', '');
    hookForm.setValue('number', '');
    hookForm.setValue('complement', '');
  };

  return (
    <Flex flexDir="column" gap={4}>
      <Text
        color="brand.800"
        fontSize="2xl"
        fontWeight={600}
        textAlign={{ base: 'center', lg: 'start' }}
      >
        {t('billingAddress')}
      </Text>

      <Grid templateColumns="repeat(6, 1fr)" gap={4} w="full">
        <GridItem colSpan={6}>
          <Checkbox
            name="sameAddress"
            label={t('useMyAddress')}
            p={4}
            gap={4}
          />
        </GridItem>

        {sameAddress && (
          <GridItem colSpan={6}>
            <Flex
              flexDir="column"
              borderRadius="lg"
              p={4}
              bg="brand.50"
              border="1px solid"
              borderColor="neutral.400"
            >
              <Flex
                flexDir={{ base: 'column-reverse', lg: 'row' }}
                justifyContent={{ lg: 'space-between' }}
                alignItems={{ base: 'start', lg: 'center' }}
                mb={4}
              >
                <Text color="brand.800" fontSize="lg" fontWeight="semibold">
                  {currentCompany?.Company?.companyName}
                </Text>
              </Flex>

              <Text color="brand.800" fontSize="sm" fontWeight="medium">
                {t('document')}:{' '}
                {maskDocument(currentCompany?.Company?.documentNumber ?? '')}
              </Text>
              <Text color="brand.800" fontSize="sm" fontWeight="medium">
                {t('phone')}:{' '}
                {maskTelephone(currentCompany?.Company?.phones?.[0] ?? '')}
              </Text>
            </Flex>
          </GridItem>
        )}

        {!sameAddress && (
          <>
            <GridItem
              colSpan={{ base: 3, md: 2 }}
              display="flex"
              gap={2}
              alignItems="end"
            >
              <Input
                label={t('cross-seller/payment:postalCode')}
                name="postalCode"
                type="mask"
                mask="99999-999"
                placeholder={t('cross-seller/payment:postalCodePlaceholder')}
              />{' '}
              <IconButton
                aria-label="Buscar endereço"
                icon={<Icon as={Trash} />}
                onClick={handleClearPostalCode}
              />
            </GridItem>

            <GridItem colSpan={{ base: 2, md: 2 }}>
              <Input
                label={t('cross-seller/payment:number')}
                name="number"
                placeholder={t('cross-seller/payment:numberPlaceholder')}
              />
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <Input
                label={t('cross-seller/payment:complement')}
                name="complement"
                placeholder={t('cross-seller/payment:complementPlaceholder')}
              />
            </GridItem>

            {postalCodePartial && (
              <>
                <GridItem colSpan={{ base: 6, md: 3 }}>
                  <Input
                    label={t('cross-seller/payment:street')}
                    name="street"
                    placeholder={t('cross-seller/payment:streetPlaceholder')}
                  />
                </GridItem>
                <GridItem colSpan={{ base: 6, md: 3 }}>
                  <Input
                    label={t('cross-seller/payment:district')}
                    name="district"
                    placeholder={t('cross-seller/payment:districtPlaceholder')}
                  />
                </GridItem>
              </>
            )}
            {hookForm.getValues('city') && (
              <GridItem colSpan={4}>
                <AddressCard
                  city={hookForm.watch('city')}
                  state={hookForm.watch('state')}
                  street={hookForm.watch('street')}
                  district={hookForm.watch('district')}
                  number={hookForm.watch('number')}
                  complement={hookForm.watch('complement')}
                />
              </GridItem>
            )}
          </>
        )}
      </Grid>
    </Flex>
  );
};
